import { ZIndex } from "@primeuix/utils/zindex";

import config from "@/config";
import { waitForElement } from "@/lib/wait-for-element";

import { loadScript } from "./load-script";
import { rememberSource } from "./provenance";
import { type Connector, PickCancelled } from "./types";

const GIS_SRC = "https://accounts.google.com/gsi/client";
const GAPI_SRC = "https://apis.google.com/js/api.js";
const FILES_ENDPOINT = "https://www.googleapis.com/drive/v3/files";

const PDF = "application/pdf";
const DOCX =
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const XLSX =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

// Google-native files have no bytes of their own; export to formats we can read.
const EXPORT_AS: Record<string, { mimetype: string; extension: string }> = {
    "application/vnd.google-apps.document": {
        mimetype: DOCX,
        extension: "docx",
    },
    "application/vnd.google-apps.spreadsheet": {
        mimetype: XLSX,
        extension: "xlsx",
    },
    "application/vnd.google-apps.presentation": {
        mimetype: PDF,
        extension: "pdf",
    },
    "application/vnd.google-apps.drawing": { mimetype: PDF, extension: "pdf" },
};

const PICKABLE = [
    ...Object.keys(EXPORT_AS),
    PDF,
    DOCX,
    XLSX,
    "text/plain",
    "text/markdown",
    "text/csv",
].join(",");

// The picker renders into document.body; lift it above PrimeVue overlays.
const PRIMEVUE_MODAL_Z_INDEX = 1100;

export interface PickedDoc {
    id: string;
    name: string;
    mimeType: string;
    url?: string;
}

export interface DownloadRequest {
    url: string;
    filename: string;
    mimetype: string;
}

const withExtension = (name: string, extension: string) =>
    name.toLowerCase().endsWith(`.${extension}`)
        ? name
        : `${name}.${extension}`;

export function downloadRequest(doc: PickedDoc): DownloadRequest {
    const exported = EXPORT_AS[doc.mimeType];
    if (exported) {
        return {
            url: `${FILES_ENDPOINT}/${doc.id}/export?mimeType=${encodeURIComponent(exported.mimetype)}`,
            filename: withExtension(doc.name, exported.extension),
            mimetype: exported.mimetype,
        };
    }
    return {
        url: `${FILES_ENDPOINT}/${doc.id}?alt=media`,
        filename: doc.name,
        mimetype: doc.mimeType,
    };
}

// Drive's reason is in the body: API disabled and no access need different fixes.
const MAX_REASON_CHARS = 300;

async function reasonFrom(response: Response): Promise<string> {
    try {
        const body = await response.text();
        const message = (JSON.parse(body) as { error?: { message?: string } })
            ?.error?.message;
        return (message ?? body).slice(0, MAX_REASON_CHARS);
    } catch {
        return "";
    }
}

// One at a time: Drive rate-limits bursts of exports.
export async function downloadDocs(
    docs: PickedDoc[],
    token: string
): Promise<File[]> {
    const files: File[] = [];
    for (const doc of docs) {
        const { url, filename, mimetype } = downloadRequest(doc);
        const response = await fetch(url, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
            const reason = await reasonFrom(response);
            throw new Error(
                `Google Drive would not hand over "${doc.name}" (${response.status})` +
                    (reason ? `: ${reason}` : ".")
            );
        }
        files.push(
            rememberSource(
                new File([await response.blob()], filename, { type: mimetype }),
                { source: "google-drive", url: doc.url }
            )
        );
    }
    return files;
}

interface TokenResponse {
    access_token?: string;
    expires_in?: number;
    error?: string;
}

interface TokenClient {
    requestAccessToken(): void;
}

interface GoogleIdentity {
    accounts?: {
        oauth2?: {
            initTokenClient(options: {
                client_id: string;
                scope: string;
                prompt?: string;
                callback: (response: TokenResponse) => void;
                error_callback?: (error: { type?: string }) => void;
            }): TokenClient;
        };
    };
}

// Identity Services has no types here, and @types/google.picker claims `google`.
const identity = () =>
    (window as unknown as { google?: GoogleIdentity }).google?.accounts?.oauth2;

// Faster than this, Google closed the window itself.
const SELF_CLOSE_MS = 1500;
const PICKER_LOAD_TIMEOUT_MS = 15_000;

export type TokenFailure =
    { cancelled: true } | { cancelled: false; message: string };

// A refused origin is reported as `popup_closed`, like a person closing the
// window; only the timing tells them apart.
export function classifyTokenError(
    type: string | undefined,
    elapsedMs: number
): TokenFailure {
    if (type === "popup_failed_to_open") {
        return {
            cancelled: false,
            message: "The browser blocked Google's sign-in window.",
        };
    }
    if (type === "popup_closed" && elapsedMs >= SELF_CLOSE_MS) {
        return { cancelled: true };
    }
    return {
        cancelled: false,
        message: `Google closed its sign-in window. Check that ${window.location.origin} is an authorised JavaScript origin on the OAuth client.`,
    };
}

let token: { value: string; expiresAt: number } | null = null;

const accessToken = async (): Promise<string> => {
    if (token && token.expiresAt > Date.now() + 60_000) return token.value;

    await loadScript(GIS_SRC);
    const oauth2 = identity();
    if (!oauth2) throw new Error("Google sign-in did not load.");

    const started = Date.now();
    return new Promise<string>((resolve, reject) => {
        oauth2
            .initTokenClient({
                client_id: config.gDrivePicker.clientId,
                scope: config.gDrivePicker.scope,
                prompt: "select_account",
                callback: (response) => {
                    if (!response.access_token) {
                        reject(
                            new Error(
                                response.error ??
                                    "Google did not grant access to your files."
                            )
                        );
                        return;
                    }
                    token = {
                        value: response.access_token,
                        expiresAt:
                            Date.now() + (response.expires_in ?? 3600) * 1000,
                    };
                    resolve(response.access_token);
                },
                error_callback: (error) => {
                    const failure = classifyTokenError(
                        error?.type,
                        Date.now() - started
                    );
                    reject(
                        failure.cancelled
                            ? new PickCancelled()
                            : new Error(failure.message)
                    );
                },
            })
            .requestAccessToken();
    });
};

let pickerApi: Promise<void> | null = null;

const loadPickerApi = (): Promise<void> => {
    pickerApi ??= loadScript(GAPI_SRC)
        .then(
            () =>
                new Promise<void>((resolve, reject) =>
                    gapi.load("picker", {
                        callback: () => resolve(),
                        onerror: () =>
                            reject(
                                new Error("Google's file picker did not load.")
                            ),
                        timeout: PICKER_LOAD_TIMEOUT_MS,
                        ontimeout: () =>
                            reject(
                                new Error(
                                    "Google's file picker took too long to load."
                                )
                            ),
                    })
                )
        )
        .catch((error) => {
            // Forget the failure, as loadScript does, so the next click can retry.
            pickerApi = null;
            throw error;
        });
    return pickerApi;
};

const lift = async (): Promise<HTMLElement | null> => {
    const dialog = await waitForElement<HTMLElement>(".picker-dialog");
    ZIndex.set("modal", dialog, PRIMEVUE_MODAL_Z_INDEX);
    return dialog;
};

const openPicker = async (accessToken: string): Promise<PickedDoc[]> => {
    await loadPickerApi();
    const lifted = lift();

    return new Promise<PickedDoc[]>((resolve) => {
        const picker = new google.picker.PickerBuilder()
            .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
            .addView(
                new google.picker.DocsView()
                    .setIncludeFolders(true)
                    .setSelectFolderEnabled(false)
                    .setMimeTypes(PICKABLE)
            )
            .setOAuthToken(accessToken)
            .setDeveloperKey(config.gDrivePicker.apiKey)
            .setCallback((data: google.picker.ResponseObject) => {
                const action = data[google.picker.Response.ACTION];
                if (
                    action !== google.picker.Action.PICKED &&
                    action !== google.picker.Action.CANCEL
                ) {
                    return;
                }
                lifted.then((dialog) => dialog && ZIndex.clear(dialog));
                picker.dispose();
                resolve(
                    action === google.picker.Action.PICKED
                        ? (data.docs ?? []).map((doc) => ({
                              id: doc.id,
                              name: doc.name,
                              mimeType: doc.mimeType,
                              url: doc.url,
                          }))
                        : []
                );
            })
            .build();

        picker.setVisible(true);
    });
};

export const googleDrive: Connector = {
    id: "google-drive",
    label: "Google Drive",
    icon: "pi pi-google",
    get isConfigured() {
        return Boolean(
            config.gDrivePicker?.clientId && config.gDrivePicker?.apiKey
        );
    },
    preload: async () => {
        await Promise.all([loadScript(GIS_SRC), loadPickerApi()]);
    },
    forget: () => {
        token = null;
    },
    pick: async () => {
        try {
            const granted = await accessToken();
            const docs = await openPicker(granted);
            if (!docs.length) {
                // Maybe the wrong account: ask again next time.
                token = null;
                return [];
            }
            return await downloadDocs(docs, granted);
        } catch (e) {
            if (e instanceof PickCancelled) return [];
            throw e;
        }
    },
};
